import { Routes, Route } from 'react-router-dom'
import { useEffect, lazy, Suspense, memo, startTransition } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '@/components/layout/Layout'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import PublicRoute from '@/components/auth/PublicRoute'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { setIsMobile } from '@/features/ui/uiSlice'
import { setCurrentLanguage } from '@/features/lang/langSlice'
import { checkAuthStatus } from '@/features/auth/authSlice'

// 🚀 OPTIMIZATION: Lazy load all pages for better performance
const Login = lazy(() => import('@/auth/Login'))
const Register = lazy(() => import('@/auth/Register'))
const Dashboard = lazy(() => import('@/app/EnhancedDashboard'))
const Category = lazy(() => import('@/app/Category'))
const Profile = lazy(() => import('@/app/OptimizedProfile'))
const UserDetail = lazy(() => import('@/app/UserDetail'))
const Coaching = lazy(() => import('@/app/Coaching'))
const Reports = lazy(() => import('@/app/Reports'))
const Settings = lazy(() => import('@/app/Settings'))
const Configuration = lazy(() => import('@/app/Configuration'))
const Channels = lazy(() => import('@/app/Channels'))
const TeamMapPage = lazy(() => import('@/features/teammap/TeamMapPage'))
const JiraPeopleOverview = lazy(() => import('@/features/jira/JiraPeopleOverview'))
const JiraPersonDetail = lazy(() => import('@/features/jira/JiraPersonDetailEnhanced'))
const JiraDashboard = lazy(() => import('@/features/jira/JiraDashboard').then(m => ({ default: m.JiraDashboard })))
const JiraPerformanceDashboard = lazy(() => import('@/features/jira/JiraPerformanceDashboard'))

// 🚀 OPTIMIZATION: Enhanced loading fallback component with better UX
const PageLoadingFallback = memo(function PageLoadingFallback() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center space-y-4 animate-in">
        {/* Logo */}
        <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-lg mx-auto">
          <span className="text-white font-bold text-lg tracking-tight">il</span>
        </div>
        
        {/* Loading Animation */}
        <div className="space-y-3">
          <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mx-auto">
            <div className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full animate-pulse"></div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
            Loading feedbeq...
          </p>
        </div>
      </div>
    </div>
  )
})

// 🚀 OPTIMIZATION: Route components wrapped with Suspense for better code splitting
const SuspenseRoute = memo(function SuspenseRoute({ 
  children 
}: { 
  children: React.ReactNode 
}) {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      {children}
    </Suspense>
  )
})

function App() {
  const dispatch = useAppDispatch()
  const { theme } = useAppSelector((state) => state.ui)
  const { currentLanguage } = useAppSelector((state) => state.lang)
  const { i18n } = useTranslation()

  // Check auth status on app load with startTransition for better UX
  useEffect(() => {
    startTransition(() => {
      dispatch(checkAuthStatus())
    })
  }, [dispatch])

  // Handle responsive layout with debounced resize handler
  useEffect(() => {
    const handleResize = () => {
      startTransition(() => {
        dispatch(setIsMobile(window.innerWidth < 1024))
      })
    }

    // Initial check
    handleResize()
    
    // Debounced resize handler for better performance
    let timeoutId: number
    const debouncedResize = () => {
      clearTimeout(timeoutId)
      timeoutId = window.setTimeout(handleResize, 150)
    }

    window.addEventListener('resize', debouncedResize, { passive: true })
    
    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(timeoutId)
    }
  }, [dispatch])

  // Sync theme on mount with smooth transition
  useEffect(() => {
    const documentElement = document.documentElement
    
    // Apply theme with transition
    documentElement.style.transition = 'background-color 0.3s ease, color 0.3s ease'
    documentElement.classList.toggle('dark', theme === 'dark')
    
    // Cleanup transition after it completes
    const timeoutId = setTimeout(() => {
      documentElement.style.transition = ''
    }, 300)
    
    return () => clearTimeout(timeoutId)
  }, [theme])

  // Handle language changes with startTransition for better UX
  useEffect(() => {
    const changeLanguage = async () => {
      if (i18n.language !== currentLanguage) {
        startTransition(() => {
          i18n.changeLanguage(currentLanguage)
        })
      }
    }
    changeLanguage()
  }, [currentLanguage, i18n])

  // Sync Redux language state with i18n
  useEffect(() => {
    if (i18n.language && i18n.language !== currentLanguage) {
      startTransition(() => {
        dispatch(setCurrentLanguage(i18n.language))
      })
    }
  }, [i18n.language, currentLanguage, dispatch])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <Routes>
        {/* Public Routes (Authentication) */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <SuspenseRoute>
                <Login />
              </SuspenseRoute>
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <SuspenseRoute>
                <Register />
              </SuspenseRoute>
            </PublicRoute>
          } 
        />

        {/* Protected Routes (Application) */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard Routes */}
          <Route 
            index 
            element={
              <SuspenseRoute>
                <Dashboard />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="dashboard" 
            element={
              <SuspenseRoute>
                <Dashboard />
              </SuspenseRoute>
            } 
          />
          
          {/* Feature Routes */}
          <Route 
            path="channels" 
            element={
              <SuspenseRoute>
                <Channels />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="category/:id" 
            element={
              <SuspenseRoute>
                <Category />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="profile" 
            element={
              <SuspenseRoute>
                <Profile />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="coaching" 
            element={
              <SuspenseRoute>
                <Coaching />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="reports" 
            element={
              <SuspenseRoute>
                <Reports />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="settings" 
            element={
              <SuspenseRoute>
                <Settings />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="configuration" 
            element={
              <SuspenseRoute>
                <Configuration />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="teammap" 
            element={
              <SuspenseRoute>
                <TeamMapPage />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="jira" 
            element={
              <SuspenseRoute>
                <JiraPerformanceDashboard />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="jira/overview" 
            element={
              <SuspenseRoute>
                <JiraDashboard />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="jira/people" 
            element={
              <SuspenseRoute>
                <JiraPeopleOverview />
              </SuspenseRoute>
            } 
          />
          <Route 
            path="jira/person/:accountId" 
            element={
              <SuspenseRoute>
                <JiraPersonDetail />
              </SuspenseRoute>
            } 
          />
        </Route>

        {/* Standalone User Detail Page (No Layout) */}
        <Route 
          path="/user/:userId" 
          element={
            <ProtectedRoute>
              <SuspenseRoute>
                <UserDetail />
              </SuspenseRoute>
            </ProtectedRoute>
          } 
        />

        {/* Fallback for unmatched routes */}
        <Route 
          path="*" 
          element={
            <ProtectedRoute>
              <SuspenseRoute>
                <Dashboard />
              </SuspenseRoute>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </div>
  )
}

export default App 